import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import useSWR from "swr";

import { useEffect, useMemo, useRef } from "react";

import { twMerge } from "tailwind-merge";
import { useToggle, useUpdateEffect } from "usehooks-ts";

import { Loader } from "components/customElements/loader";
import DiscussionEditor from "components/DiscussionEditor";
import DiscussionSnippet from "components/DiscussionSnippet";
import { getInsightUrl } from "components/InsightPage";
import ProfilePicture from "components/ProfilePicture";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useInfiniteList from "hooks/useInfiniteList";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { USE_EMBER_IFRAME } from "consts";
import { resourceDiscussionsInclude } from "consts/included";
import profileAvatar from "assets/avatar.png";

import styles from "./styles.module.scss";

const DiscussionModal = dynamic(() => import("components/DiscussionModal"));
const DiscussionModalIframe = dynamic(
  () => import("components/DiscussionModalIframe")
);

export const insightDiscussionsUrl = (slug: string, pageIndex: number) => {
  const url = new ApiEndpoint(`/api/v2/insights/${slug}/comment-threads`);
  url.searchParams.append("include", resourceDiscussionsInclude);
  url.searchParams.append("page[size]", "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("sort", "-updated-at");

  return url.href;
};

const InsightDiscussionsList = ({
  slug,
  className,
  customBodyClassName,
  snippetStyle,
  composerOnTop,
  miniComposer,
  showComposerByDefault,
}: {
  slug: string;
  className?: string;
  customBodyClassName?: string;
  snippetStyle?: "default" | "v2" | "compact" | "compactV2";
  composerOnTop?: boolean;
  miniComposer?: boolean;
  showComposerByDefault?: boolean;
}) => {
  const { account: loggedInAccount } = useAuthentication();

  const { data: phoneNumber, isValidating: isPhoneNumberValidating } = useSWR(
    loggedInAccount?.id
      ? `/api/v2/accounts/${loggedInAccount?.id}/phone-number`
      : null,
    baseFetcher
  );

  const isUnverifiedPreviewPass =
    loggedInAccount?.isPreviewPass &&
    !isPhoneNumberValidating &&
    !(phoneNumber?.verified || phoneNumber?.skipVerification);

  const router = useRouter();
  const itemId = router.query.discussion as string;

  const { data: insight } = useApi(getInsightUrl(slug), baseFetcher);

  const {
    infiniteList,
    isValidating,
    mutate,
    isEmpty,
    isRefreshing,
    prependItem,
  } = useInfiniteList(
    (pageIndex) => insightDiscussionsUrl(slug, pageIndex + 1),
    {
      fetcher: (endpoint) =>
        baseFetcher(`${endpoint}&${new Date().getMilliseconds()}`),
      loadingIndicator: () => <Loader numberOfItems={5} />,
      endIndicator: () => <></>,
      emptyIndicator: () => <></>,
      pageRender(pageData) {
        if (!pageData) return null;

        return pageData?.data?.map?.((discussion: any) => (
          <DiscussionSnippet
            key={discussion.id}
            discussion={discussion}
            className={
              snippetStyle === "v2"
                ? "mb-[0px] mt-[25px] cursor-default"
                : "first-of-type:pt-0"
            }
            hideSource
            onReply={() => {
              if (snippetStyle !== "v2") mutate();
            }}
            showInlineComposer={snippetStyle !== "v2"}
            customBodyClassName={customBodyClassName}
            style={snippetStyle}
            hideInlineReplyCta={snippetStyle === "v2"}
            onClick={() => {
              router.query.discussion = discussion?.id;
              router.push(router, undefined, { shallow: true, scroll: false });
            }}
          />
        ));
      },
    }
  );

  const [showComposer, toggleComposer, setShowComposer] = useToggle(
    showComposerByDefault
  );

  useUpdateEffect(() => {
    if (!isValidating && isEmpty && !showComposer) {
      setShowComposer(true);
    }
  }, [isEmpty, isValidating, setShowComposer, showComposer]);

  const editorRef = useRef(null);
  useEffect(() => {
    if (showComposerByDefault) {
      setTimeout(() => {
        (editorRef.current as any)?.focus?.();
      }, 100);
    }
  }, [showComposerByDefault]);

  const composer = useMemo(
    () =>
      loggedInAccount?.canPostComment &&
      !isUnverifiedPreviewPass && (
        <div className={twMerge("min-h-[400px]", className)}>
          {showComposer || miniComposer ? (
            <DiscussionEditor
              editorRef={editorRef}
              commentable={insight}
              afterSubmit={(comment) => {
                prependItem(comment);
              }}
              onlyAttachments
              style="v3"
              bypassFocus={!showComposerByDefault}
              blurClassName="h-[35px] py-[7.5px] rounded-[10px]"
              blurAfterSubmit
            />
          ) : (
            <div className="flex">
              <ProfilePicture
                src={loggedInAccount?.avatarUrl?.small || profileAvatar}
              />
              <button
                className={twMerge(
                  "border border-solid border-smart-teal",
                  "bg-transparent hover:bg-smart-teal",
                  "text-smart-teal hover:text-white text-[13px] font-bold leading-[1]",
                  "rounded-full px-[1.25em] py-[0.75em]"
                )}
                onClick={toggleComposer}
              >
                Start New Discussion
              </button>
            </div>
          )}
        </div>
      ),
    [
      prependItem,
      className,
      insight,
      isUnverifiedPreviewPass,
      loggedInAccount?.avatarUrl?.small,
      loggedInAccount?.canPostComment,
      miniComposer,
      showComposer,
      showComposerByDefault,
      toggleComposer,
    ]
  );

  return (
    <>
      {composerOnTop && loggedInAccount?.canPostComment && (
        <div
          className={twMerge(
            styles.composer,
            "rounded-[15px] border border-solid border-[#ddd] p-[15px]"
          )}
        >
          {composer}
        </div>
      )}
      <div className={twMerge(snippetStyle === "v2" && "")}>
        <div className="">
          {isRefreshing ? (
            <Loader />
          ) : (
            <>
              {loggedInAccount?.canPostComment && !isEmpty && (
                <div className="flex md:justify-end"></div>
              )}{" "}
              {infiniteList}
            </>
          )}
        </div>
      </div>
      {itemId &&
        (loggedInAccount?.isInternalUser || !USE_EMBER_IFRAME ? (
          <DiscussionModal id={itemId} style="v2" />
        ) : (
          <DiscussionModalIframe id={itemId} onUpdate={() => mutate()} />
        ))}
      {!composerOnTop && loggedInAccount?.canPostComment && composer}
    </>
  );
};

export default InsightDiscussionsList;
